import * as qs from 'query-string';
import { Component, FormEvent } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, DashActionCreators, IAddressValidationResponse } from '@nkr/store/index';
import { IBaseResponse, IDashRegistration, IState, IUsPsAddressResponse } from '@nkr/models/index';
import { DashDonationType } from '@nkr/enums/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Row, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AddressValidation, { IAddress } from '@nkr/components/address-validation';
import JoditEditor from '@nkr/components/editor';
import config from '@nkr/views/ticketing/jodit-editor-config';

interface IOwnProps {
    centerId: number;
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getById: (id: number) => Promise<any>
    getLookupData: (centerId: number) => Promise<any>,
    shipJugOnly: (id: number, addressLine1: string, city: string, state: number, postalCode: string) => Promise<IBaseResponse>,
    startPwl: (id: number, addressLine1: string, city: string, state: number, postalCode: string, emailContent: string) => Promise<IBaseResponse>,
    validateAddress: (addressLine1: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IOwnState {
    donor: IDashRegistration,
    addressLine1: string,
    city: string,
    stateId: number,
    postalCode: string,
    action: string,
    collectAddress: boolean,
    emailContent: string,
    stateList: IState[],
    disableSubmit: boolean,
    addressValidation: {
        show: boolean,
        response: IUsPsAddressResponse,
        submittedAddress: IAddress
    }
}

class ConfirmAddress extends Component<TProps, IOwnState>{
    readonly state: IOwnState = {
        donor: null,
        addressLine1: '',
        city: '',
        stateId: 0,
        postalCode: '',
        action: 'ShipJug',
        collectAddress: false,
        emailContent: '',
        stateList: [],
        disableSubmit: false,
        addressValidation: {
            show: false,
            response: null,
            submittedAddress: null
        }
    }

    componentDidMount(): void {
        this.getData();
    }

    getData = () => {
        let parsed = qs.parse(window.location.search);

        let action = parsed.action != null ? parsed.action : 'ShipJug';
        let id = parsed.id != null ? parseInt(parsed.id) : 0;

        this.props.showLoading('One moment please...').then(() => {
            Promise.all([
                this.props.getById(id),
                this.props.getLookupData(this.props.centerId)
            ])
                .then(responses => {
                    let registrationResponse = responses[0];

                    let donor = registrationResponse.data.results;

                    let lookupResponse = responses[1];

                    let { stateList, dashIntakeConfig } = lookupResponse.data.results;

                    let { addressLine1, city, stateId, postalCode, donationTypeId } = registrationResponse.data.results;

                    let emailContent = `Hi ${donor.firstName},<br/>Thank you for registering to be a potential ${donor.donationType.toLowerCase()} donor with ${donor.center.centerName}.<br/>Because of an unexpectedly high volume of donor candidates we, unfortunately, can’t call everyone.<br/>Please continue with the next phase of the process by obtaining your labs. After labs are completed and results received a team member will reach out to you by email/phone.`;

                    this.setState({
                        action,
                        collectAddress: action == 'ShipJug' || (donationTypeId == DashDonationType.Kidney && !dashIntakeConfig.useCystatinC),
                        donor,
                        addressLine1, city, stateId, postalCode,
                        emailContent,
                        stateList
                    });
                }).finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    getDonorDetails = () => {
        if (this.state.donor == null) {
            return {
                firstName: '',
                lastName: '',
                registrationDateTime: '',
                currentRegistrationStepDesc: ''
            };
        }

        let { firstName, lastName, registrationDateTime, currentRegistrationStepDesc } = this.state.donor;

        return {
            firstName,
            lastName,
            registrationDateTime,
            currentRegistrationStepDesc
        };
    }

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;

        if (name == 'address1') {
            this.setState({
                addressLine1: value
            });
        }

        if (name == 'city') {
            this.setState({
                city: value
            });
        }

        if (name == 'state') {
            this.setState({
                stateId: parseInt(value)
            });
        }

        if (name == 'postalCode') {
            this.setState({
                postalCode: value
            });
        }
    }

    handleEmailContentChanged = (emailContent: string) => {
        this.setState({
            emailContent
        });
    }

    handleSubmit = () => {
        this.setState({
            disableSubmit: true,
            addressValidation: {
                show: false,
                response: null,
                submittedAddress: null
            }
        }, () => {
            let { donor, addressLine1, city, stateId, postalCode, emailContent } = this.state;

            this.props.showLoading('Submitting your request, one moment please...').then(async () => {
                if (this.state.action == 'ShipJug') {
                    let state = this.state.stateList.find(x => x.id == stateId);
                    let stateAbbr = state != null ? state.abbreviation : '';

                    let validateResponse = await this.props.validateAddress(addressLine1, city, stateAbbr, postalCode);

                    if (validateResponse.data.success == false) {
                        this.setState({
                            disableSubmit: false,
                            addressValidation: {
                                show: true,
                                response: validateResponse.data.results,
                                submittedAddress: {
                                    street: addressLine1,
                                    street2: '',
                                    city,
                                    state: stateAbbr,
                                    postalCode
                                }
                            }
                        }, () => {
                            this.props.hideLoading();
                        });
                    } else {
                        this.props.shipJugOnly(donor.id, addressLine1, city, stateId, postalCode)
                            .then(response => {
                                if (response.data.success) {
                                    toast.success('Your request was submitted successfully, you will now be redirected to the details page.', {
                                        onClose: () => {
                                            window.location.href = `center/${this.props.centerId}/donors/donor-intake/details/${donor.id}`;
                                        }
                                    });
                                }
                            })
                            .catch(error => {
                                this.setState({
                                    disableSubmit: false
                                }, () => {
                                    toast.error(<>Unable to submit this request...<br />{error.response.data}</>, { autoClose: false });
                                });
                            })
                            .finally(() => {
                                this.props.hideLoading();
                            });
                    }
                } else {
                    this.props.startPwl(donor.id, addressLine1, city, stateId, postalCode, emailContent)
                        .then(response => {
                            if (response.data.success) {
                                toast.success('Your request was submitted successfully, you will now be redirected to the details page.', {
                                    onClose: () => {
                                        window.location.href = `center/${this.props.centerId}/donors/donor-intake/details/${donor.id}`;
                                    }
                                });
                            }
                        })
                        .catch(error => {
                            this.setState({
                                disableSubmit: false
                            }, () => {
                                toast.error(<>Unable to submit this request...<br />{error.response.data}</>, { autoClose: false });
                            });
                        })
                        .finally(() => {
                            this.props.hideLoading();
                        });
                }
            });
        });
    }

    handleCancelAddressSelection = () => {
        this.setState({
            addressValidation: { ...this.state.addressValidation, show: false }
        });
    }

    handleSubmitWithAddress = (recommended: boolean) => {
        let { response, submittedAddress } = this.state.addressValidation;

        let addressLine1 = recommended ? response.address1 : submittedAddress.street;
        let city = recommended ? response.city : submittedAddress.city;
        let state = recommended ? response.state : submittedAddress.state;
        let stateId = this.state.stateList.find(x => x.abbreviation == state).id;
        let postalCode = recommended ? response.zip5 : submittedAddress.postalCode;

        this.setState({
            disableSubmit: true,
            addressValidation: { ...this.state.addressValidation, show: false }
        }, () => {
            this.props.showLoading('Submitting your request, one moment please...').then(() => {
                this.props.shipJugOnly(this.state.donor.id, addressLine1, city, stateId, postalCode)
                    .then(response => {
                        if (response.data.success) {
                            toast.success('Your request was submitted successfully, you will now be redirected to the details page.', {
                                onClose: () => {
                                    window.location.href = `center/${this.props.centerId}/donors/donor-intake/details/${this.state.donor.id}`
                                }
                            });
                        }
                    })
                    .catch(error => {
                        this.setState({
                            disableSubmit: false
                        }, () => {
                            toast.error(<>Unable to submit this request...<br />{error.response.data}</>, { autoClose: false });
                        });
                    })
                    .finally(() => {
                        this.props.hideLoading();
                    });
            });
        });

    }

    handleCancel = () => {
        window.location.href = '/center/' + this.props.centerId + '/donors/donor-intake/to-do';
    }

    render = () => {
        const joditConfig = { ...config, height: '300px', removeButtons: [...config.removeButtons, 'copyformat', 'preview', 'find'] };

        const { firstName, lastName, registrationDateTime, currentRegistrationStepDesc } = this.getDonorDetails();

        return (
            <>
                <form>
                    <div className='container' style={{ paddingBottom: 20 }}>
                        {this.state.action == 'ShipJug' && (
                            <h4>Ship Jug &amp; Go to Center</h4>
                        )}

                        {this.state.action == 'StartPwl' && (
                            <h4>Start Pre-Workup Labs</h4>
                        )}

                        <Well bsSize='sm'>
                            <Row>
                                <Col xs={12} lg={2}>Donor:</Col>
                                <Col xs={12} lg={9}>{firstName} {lastName}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} lg={2}>Registered:</Col>
                                <Col xs={12} lg={9}>{registrationDateTime}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} lg={2}>Registration Step:</Col>
                                <Col xs={12} lg={9}>{currentRegistrationStepDesc}</Col>
                            </Row>
                        </Well>

                        {this.state.collectAddress && (
                            <div>
                                {this.state.action == 'ShipJug' && (
                                    <Alert bsStyle='info'>
                                        Please confirm that you wish to ship a jug to this donor candidate.
                                        With this option, we will send you a notification when the jug arrives, but follow-up
                                        will be the responsibility of the center. We won't send follow-up e-mails to the
                                        donor candidate.
                                    </Alert>
                                )}

                                {this.state.action == 'StartPwl' && (
                                    <Alert bsStyle='info'>
                                        Your configuration settings are set to utilize 24-hour urine instead of Cystatin-C. So, this process will ship a urine jug to the donor and e-mail the donor again when we have notification of arrival.
                                    </Alert>
                                )}

                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <ControlLabel>Address</ControlLabel>
                                            <FormControl
                                                id='address1'
                                                name='address1'
                                                placeholder='Address'
                                                value={this.state.addressLine1}
                                                onChange={this.handleFieldChange} />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <ControlLabel>City</ControlLabel>
                                            <FormControl
                                                id='city'
                                                name='city'
                                                placeholder='City'
                                                value={this.state.city}
                                                onChange={this.handleFieldChange} />
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <ControlLabel>State</ControlLabel>
                                            <FormControl
                                                componentClass='select'
                                                id='state'
                                                name='state'
                                                value={this.state.stateId}
                                                onChange={this.handleFieldChange}>
                                                <option value=''>Please Select</option>
                                                {this.state.stateList.map(state =>
                                                    <option key={'state-' + state.id} value={state.id}>{state.abbreviation}</option>
                                                )}
                                            </FormControl>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup>
                                            <ControlLabel>Postal Code</ControlLabel>
                                            <FormControl
                                                id='postalCode'
                                                name='postalCode'
                                                placeholder='Postal Code'
                                                value={this.state.postalCode}
                                                onChange={this.handleFieldChange} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {this.state.action != 'ShipJug' && (
                            <Row>
                                <Col xs={12}>
                                    <Alert bsStyle='danger'>
                                        WARNING: Whatever you type in the white area below will go to the donor.  Please review and make sure that it says what you want it to say.  The footer
                                        in gray will go to the donor regardless to ensure they have the link to continue to the next step.  The email will go to the donor from
                                        registrations@donor-screen.com.
                                    </Alert>

                                    <label htmlFor='emailContent'>Email Content</label>

                                    <JoditEditor
                                        value={this.state.emailContent}
                                        config={joditConfig}
                                        onChange={this.handleEmailContentChanged}
                                        onBlur={() => null} />

                                    <div style={{ border: 'solid #CCC', borderWidth: '0px 1px 1px 1px', padding: 10, backgroundColor: '#EEE' }}>
                                        To view your next steps and download your lab orders, please <span style={{ color: 'blue', textDecoration: 'underline' }}>click here</span>.<br />
                                        <br />
                                        Thank you for your interest in giving others the opportunity for a healthier life through donation.</div>
                                </Col>
                            </Row>
                        )}

                        <Row>
                            <Col md={12} style={{ paddingTop: 20 }}>
                                <div className='text-center'>
                                    <Button bsStyle='primary' bsSize='large' disabled={this.state.disableSubmit} onClick={this.handleSubmit}>Submit</Button>{' '}
                                    <Button bsSize='large' onClick={this.handleCancel}>Cancel</Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>

                <AddressValidation
                    show={this.state.addressValidation.show}
                    response={this.state.addressValidation.response}
                    submittedAddresss={this.state.addressValidation.submittedAddress}
                    onUseAddress={this.handleSubmitWithAddress}
                    onCancel={this.handleCancelAddressSelection}
                />
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getById: (id) => {
            return dispatch(DashActionCreators.getById(id));
        },
        getLookupData: (centerId) => {
            return dispatch(DashActionCreators.getDonorIntakeLookupData(centerId));
        },
        shipJugOnly: (id, addressLine1, city, state, postalCode) => {
            return dispatch(DashActionCreators.shipJugOnly(id, addressLine1, city, state, postalCode));
        },
        startPwl: (id, addressLine1, city, state, postalCode, emailContent) => {
            return dispatch(DashActionCreators.startPwl(id, addressLine1, city, state, postalCode, emailContent));
        },
        validateAddress: (addressLine1, city, state, postalCode) => {
            return dispatch(DashActionCreators.validateAddress(addressLine1, '', city, state, postalCode));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(ConfirmAddress);
